import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { logout } from '@features/authentication/store/authentication.slice';

import type { Investigation, TimeUnit } from '../api/files.types';
import type { FileStateFilter } from '../utils/constants';

export type FileTypeKey = 'insights' | 'investigations' | 'inquiries';

export interface FilesFilters {
  forCurrentUser: boolean;
  filters?: Partial<Record<FileTypeKey, FileStateFilter>>;
  timeAmount: number;
  timeUnit: TimeUnit;
}

interface InvestigationDetailsState {
  hideInsightParentBreadcrumb: boolean;
  eventSources: EventSourcesOpenPayload;
  insightsPanel: InsightsPanelOpenPayload;
  filesFilters: FilesFilters;
}

interface EventSourcesOpenPayload {
  isOpen: boolean;
  investigationId: string;
}

interface InsightsPanelOpenPayload {
  isOpen: boolean;
  investigation: Investigation | null;
}

const initialState: InvestigationDetailsState = {
  hideInsightParentBreadcrumb: true,
  eventSources: { isOpen: false, investigationId: '' },
  insightsPanel: { isOpen: false, investigation: null },
  filesFilters: {
    forCurrentUser: false,
    filters: {},
    timeAmount: 100,
    timeUnit: 'YEARS',
  },
};

const investigationsSlice = createSlice({
  name: 'investigations',
  initialState,
  reducers: {
    setHideInsightParentBreadcrumb: (state, action: PayloadAction<boolean>) => {
      state.hideInsightParentBreadcrumb = action.payload;
    },
    setEventSourcesOpen: (state, action: PayloadAction<EventSourcesOpenPayload>) => {
      state.eventSources = action.payload;
    },
    setInsightsPanelOpen: (state, action: PayloadAction<InsightsPanelOpenPayload>) => {
      state.insightsPanel = action.payload;
    },
    resetInsightsPanel: (state) => {
      state.insightsPanel = initialState.insightsPanel;
    },
    setSelectedFilters: (state, action: PayloadAction<FilesFilters>) => {
      const { filters, forCurrentUser, timeAmount, timeUnit } = action.payload;
      state.filesFilters.filters = filters;
      state.filesFilters.forCurrentUser = forCurrentUser;
      state.filesFilters.timeAmount = timeAmount;
      state.filesFilters.timeUnit = timeUnit;
    },
  },
  extraReducers: (builder) => {
    // When the logout action happens, reset this state to initial
    builder.addCase(logout, () => initialState);
  },
});

export const {
  setHideInsightParentBreadcrumb,
  setEventSourcesOpen,
  setInsightsPanelOpen,
  setSelectedFilters,
  resetInsightsPanel,
} = investigationsSlice.actions;
export const investigationsReducer = investigationsSlice.reducer;
